import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Office, PDF and CSV previews (Word / Excel / PowerPoint / PDF / CSV / TSV),
// replacing the Google Docs viewer and the old react-pdf-js / table previews.
//
// The rendering happens in a separate, prebuilt viewer app served from this
// same origin at /office-viewer/ (source: tcp-web/office-viewer, React 18 +
// @extend-ai/react-docx / react-xlsx / react-pptx, built with Node 20 into
// public/office-viewer). It runs in an iframe because those packages need
// React 18 and a modern bundler, while this app is React 16 / react-scripts 3.
//
// This component fetches the file from its signed URL and hands the BYTES to
// the iframe with postMessage — the URL never reaches the viewer and nothing
// leaves the browser. Handshake (same origin only):
//   viewer -> here  { type: "office-viewer:ready" }
//   here -> viewer  { type: "office-viewer:open", kind, name, bytes }
//   viewer -> here  { type: "office-viewer:error", message }
//
// The file is fetched straight from S3, so the bucket must allow CORS GETs from
// the app origin — Documents/Preview.jsx already fetches text files the same way.

const VIEWER_URL = "/office-viewer/index.html";

const EXTENSIONS = {
	word: ["docx", "docm", "dotx", "dotm"],
	excel: ["xlsx", "xlsm", "xls"],
	powerpoint: ["pptx", "pptm", "ppsx", "potx", "ppt"],
	csv: ["csv", "tsv"],
	pdf: ["pdf"]
};

// Which viewer a file needs, or null when none of them can show it.
// The file NAME decides first; the mimetype is the fallback when the extension
// is missing or not one of the above (e.g. a .csv saved by Excel arrives as
// application/vnd.ms-excel). Do not use the API's `extension` attribute here —
// it reports "doc" for .docx and "xls" for .xlsx (Document#extension).
export const officeKind = (name, mimetype) => {
	const match = /\.([a-z0-9]+)$/i.exec(name || "");
	const extension = match ? match[1].toLowerCase() : "";
	const byExtension = Object.keys(EXTENSIONS).find(kind => EXTENSIONS[kind].includes(extension));
	if (byExtension) return byExtension;

	const type = mimetype || "";
	if (type.includes("wordprocessingml")) return "word";
	if (type.includes("spreadsheetml") || type.includes("ms-excel")) return "excel";
	if (type.includes("presentationml") || type.includes("ms-powerpoint")) return "powerpoint";
	if (type.includes("pdf")) return "pdf";
	if (type.includes("csv") || type.includes("tab-separated-values")) return "csv";
	return null;
};

// The viewer fills the rest of the window below where it starts (never less
// than this), so it uses the whole Preview section instead of a fixed box.
const MIN_HEIGHT = 480;
const BOTTOM_GAP = 16;

const useFillHeight = (ref, fixedHeight) => {
	const [height, setHeight] = useState(fixedHeight || MIN_HEIGHT);
	useLayoutEffect(() => {
		if (fixedHeight) return undefined;
		const measure = () => {
			if (!ref.current) return;
			const top = ref.current.getBoundingClientRect().top + window.pageYOffset;
			setHeight(Math.max(MIN_HEIGHT, Math.round(window.innerHeight - top - BOTTOM_GAP)));
		};
		measure();
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, [ref, fixedHeight]);
	return height;
};

// height: optional fixed height in px; by default the viewer fills the window.
const Office = ({ url, kind, name = "", height: fixedHeight = 0 }) => {
	const frame = useRef(null);
	const box = useRef(null);
	const height = useFillHeight(box, fixedHeight);
	// The viewer shows its own "Loading…" until the bytes arrive; only a
	// failure is rendered here.
	const [status, setStatus] = useState("ok");

	useEffect(() => {
		let cancelled = false;
		let bytes = null;
		let viewerReady = false;
		setStatus("ok");

		const send = () => {
			if (cancelled || !bytes || !viewerReady || !frame.current) return;
			const payload = bytes;
			bytes = null; // transferred: the buffer is unusable here afterwards
			frame.current.contentWindow.postMessage(
				{ type: "office-viewer:open", kind, name, bytes: payload },
				window.location.origin,
				[payload]
			);
		};

		const onMessage = event => {
			if (event.origin !== window.location.origin) return;
			if (!frame.current || event.source !== frame.current.contentWindow) return;
			const data = event.data || {};
			if (data.type === "office-viewer:ready") {
				viewerReady = true;
				send();
			} else if (data.type === "office-viewer:error") {
				console.error("Office preview failed", data.message);
				if (!cancelled) setStatus("error");
			}
		};
		window.addEventListener("message", onMessage);

		fetch(url)
			.then(response => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.arrayBuffer();
			})
			.then(buffer => {
				bytes = buffer;
				send();
			})
			.catch(error => {
				console.error("Office preview failed", error);
				if (!cancelled) setStatus("error");
			});

		return () => {
			cancelled = true;
			window.removeEventListener("message", onMessage);
		};
	}, [url, kind, name]);

	return (
		// width / flex / alignSelf: the Documents preview card is a centring flex
		// box, so without them this div shrinks to the iframe's default 300px.
		<div
			ref={box}
			className={`office-preview office-preview-${kind}`}
			style={{ width: "100%", flex: "1 1 100%", alignSelf: "stretch" }}
		>
			{status === "error" && <div className="alert alert-warning">Preview not available.</div>}
			{status !== "error" && (
				// keyed on the url so a new file gets a fresh viewer (and a fresh "ready")
				<iframe
					key={url}
					ref={frame}
					src={VIEWER_URL}
					title={name || "Document preview"}
					width="100%"
					height={height}
					style={{ border: 0, display: "block" }}
				/>
			)}
		</div>
	);
};

export default Office;
